import * as React from "react";
import {ReactElement, useEffect, useState} from "react";
import {IListStaticModule} from "../../../BackendFacade/IStaticModules";
import {moduleIcons} from "../../ProjectContent/Icons/Icons";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {NetworkData} from "../../../BackendFacade";
import {getDocumentation} from "../../Documentation/BackendFacade/apiCalls";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import AppProjectsCard from "../../../OdxComponents/CustomOdx/AllProjectsCard";
import OdxMainFilterGroup from '../../../OdxComponents/NativeOdx/Odx-MainFilterGroup'
import {odxIconSets} from '../../../OdxComponents/constants'
import {Layout} from '../Interfaces/IHubLayout'
import {transformOdxLayoutRef} from '../../../OdxComponents/odxLayoutRef'
import OdxTable from '../../../OdxComponents/NativeOdx/Table/Odx-Table'
import {OdxTableRow} from '../../../OdxComponents/NativeOdx/Table/Odx-TableRow'
import {OdxTableCell} from '../../../OdxComponents/NativeOdx/Table/Odx-Tablecell'
import OdxChip from '../../../OdxComponents/NativeOdx/Odx-Chip'
import ShowDocumentation from './Modals/ShowDocumentation'
import {IProject} from "../Interfaces/IAllProjects";
import {listModuleInstances, listProjects} from "../BackendFacade/apiCalls";
import {IModule} from "../../../BackendFacade/IModuleInstances";
import {getListOfAllModules} from "../../../Utils/modules";
import {IUsageModuleToProject} from "../Interfaces/ModulesUsage";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import {orderBy} from "../../../Utils/sorting";
import {simplifyForHtmlId} from "../../../Utils/transformer";

const LISTOFPROJECTS = "listOfProjects"

const ModuleDocumentations: React.FC = () => {
    let [availableModules, setAvailableModules] = React.useState<IListStaticModule[]>([]);
    const [backgroundLoading, setBackgroundLoading] = React.useState<boolean>(true);
    const [layout, setLayout] = React.useState(Layout.Grid);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const {setLoading, createErrorToast} = useMain();
    const [isOpen, setIsOpen] = React.useState<boolean>(true);
    const [selectedModule, setSelectedModule] = useState<IListStaticModule | null>(null);
    const [usageModules, setUsageModules] = React.useState<IUsageModuleToProject[]>([]);
    const baseId = 'hub-module-docs';

    useModuleViewTracking('hub-moduledocumentation')

    useEffect(() => {
        LoadModules()
        fetchData()
    }, []);

    useEffect(() => {
        transformOdxLayoutRef(containerRef.current)
    }, [layout, availableModules]);

    useEffect(() => {
        prepareAllModules()
    }, [availableModules]);

    const LoadModules = async () => {
        setLoading(true)
        setAvailableModules(await GlobalDictionary.getStaticModules())
        setLoading(false)
    }

    // TODO remove this function, put it into provider
    const fetchData = async () => {
        const allProjects = await getListOfAllProjects();

        // get all modules to all projects
        await gatherAllUsedModules(allProjects);
    };

    // TODO remove this function, put it into provider
    const getListOfAllProjects = async () => {
        if (GlobalDictionary.get(LISTOFPROJECTS)) {
            return GlobalDictionary.get(LISTOFPROJECTS) as IProject[];
        }
        return listProjects().then((data) => {
            if (data.success) {
                GlobalDictionary.set(LISTOFPROJECTS, data.data.value)
                return data.data.value as IProject[]
            } else {
                createErrorToast("Error", data.message);
                return [] as IProject[]
            }
        })
    }

    /**
     * Get all modules to a project
     * // TODO remove this function, put it into provider
     * @param projectId
     */
    const getProjectModules = async (projectId: string) => {
        const data = await listModuleInstances(projectId);
        if (data.success) {
            const ret = data.data.value as IModule[];
            // set the project id of every module
            ret.forEach((module) => {
                module.projectId = projectId;
            });
            return ret;
        } else {
            createErrorToast("Error", data.message);
        }
    }

    // TODO remove this function, put it into provider
    const gatherAllUsedModules = async (allProjects: IProject[]) => {
        const allModules: IModule[] = []
        const listToLoad = [] as Promise<void>[]
        allProjects.forEach((project) => {
            listToLoad.push(
                getProjectModules(project.projectId).then((modules) => {
                    allModules.push(...modules)
                })
            )
        })
        await Promise.all(listToLoad)

        const allStaticModules = getListOfAllModules()

        const usageTable: IUsageModuleToProject[] = []
        allModules.forEach((module) => {
            // find module in usageTable
            let moduleInUsageTable = usageTable.find((i) => i.moduleName === module.staticModuleId)
            if (!moduleInUsageTable) {
                moduleInUsageTable = {
                    moduleName: module.staticModuleId,
                    projects: [{name: module.projectId, count: 1}],
                    count: 1,
                }
                usageTable.push(moduleInUsageTable)
            } else {
                moduleInUsageTable.count++
                // add project to list as new entry
                if (!moduleInUsageTable.projects.find((i) => i.name === module.projectId))
                    moduleInUsageTable.projects.push({
                        name: module.projectId,
                        count: 1
                    })
                else
                    moduleInUsageTable.projects.find((i) => i.name === module.projectId).count++
            }
        })

        // add static modules that are not used
        Object.keys(allStaticModules).forEach((key) => {
            if (!usageTable.find((i) => i.moduleName === key)) {
                usageTable.push({
                    moduleName: key,
                    projects: [],
                    count: 0,
                })
            }
        })
        setUsageModules(usageTable)

    }

    const prepareAllModules = () => {
        const listOfPromises = [] as Promise<void>[]
        availableModules.forEach((module) => {
            if (!GlobalDictionary.get("Docu" + module.staticModuleId)) {
                setBackgroundLoading(true)
                listOfPromises.push(getModuleDocumentation(module.staticModuleId))
            }
        })
        Promise.all(listOfPromises).then(() => {
            setBackgroundLoading(false)
        })
    }

    const getModuleDocumentation = async (moduleId: string) => {
        await getDocumentation(moduleId).then((response: NetworkData) => {
            if (response.success) {
                GlobalDictionary.set("Docu" + moduleId, response.data.value)
            } else {
                // createErrorToast("Error", response.message); // TODO
            }
        })
    }

    const NewModuleCards = (): ReactElement => {
        availableModules = orderBy(availableModules, x => x.displayName)
        const cards = availableModules.map((module, key) => {
            const moduleKey = simplifyForHtmlId(module.staticModuleId || `module-${key}`);
            return <div id={`${baseId}-grid-card-${moduleKey}`} className={"flex flex-col h-full"} key={module.staticModuleId ?? key}>
                <AppProjectsCard key={key}
                                 title={module.displayName}
                                 description={module.description}
                                 imageSrc={
                                     moduleIcons[module.staticModuleId] ? moduleIcons[module.staticModuleId] : moduleIcons["default"]
                                 }
                                 mode={"module"}
                                 numberOfModules={usageModules.length == 0 ? -1 :
                                     usageModules.find((i) => i.moduleName === module.staticModuleId)?.count || 0}
                                 onClick={() => openModuleDocumentation(module)}
                                 idPrefix={`${baseId}-grid-card-${moduleKey}`}
                />
            </div>
        })

        return <>
            <div className={'odx-padding-top-24 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'}
                 id={`${baseId}-grid`}>
                {cards}
            </div>
        </>
    }

    const ChipAmountOfUsedProjects = (smodule: IListStaticModule): ReactElement => {
        const moduleKey = simplifyForHtmlId(smodule.staticModuleId || 'module');
        if (usageModules.length === 0) return <OdxChip size={"small"} content={"Loading"} id={`${baseId}-usage-${moduleKey}`}/>
        const amount = usageModules.find((i) => i.moduleName === smodule.staticModuleId)?.count || 0
        return <OdxChip size={"small"}
                        id={`${baseId}-usage-${moduleKey}`}
                        content={amount === 0 ? "Not used yet" : amount === 1 ? "1 Instance" : `${amount} Instances`}/>
    }

    const getListView = (): ReactElement => {
        return (
            <div className={'pt-10'} id={`${baseId}-list-wrapper`}>
                <OdxTable
                    className={'table-fixed'}
                    variant={'default'}
                    id={`${baseId}-table`}
                    headerData={[
                        {name: 'Name', title: 'Name', sortable: true},
                        {name: 'Short Description', title: 'Short Description', sortable: false},
                        {name: 'Used In Projects', title: 'Used In Projects', sortable: true}
                    ]}
                >
                    {orderBy(availableModules, x => x.displayName)
                        .map((item, index) => {
                            const moduleKey = simplifyForHtmlId(item.staticModuleId || `module-${index}`);
                            return (
                                <OdxTableRow key={item.staticModuleId ?? String(index)} onClick={() => openModuleDocumentation(item)} id={`${baseId}-row-${moduleKey}`}>
                                    <OdxTableCell data-odxLayout={"flex"} className={"items-center"} id={`${baseId}-cell-${moduleKey}-name`}>
                                        <odx-avatar
                                            size="xxl"
                                            shape="circle"
                                            variant="ghost"
                                            className="flex items-center justify-center"
                                            style={{"--size": "64px"} as React.CSSProperties}
                                            id={`${baseId}-avatar-${moduleKey}`}
                                        >
                                            {moduleIcons[item.staticModuleId] || moduleIcons["default"] ? (
                                                <img
                                                    src={moduleIcons[item.staticModuleId] ? moduleIcons[item.staticModuleId] : moduleIcons["default"]}
                                                    alt={`${item.displayName} icon`}
                                                    className="object-cover !w-[70%] !h-[70%]"
                                                />
                                            ) : (
                                                <odx-icon name="core::user" size="xxl"></odx-icon>
                                            )}
                                        </odx-avatar>
                                        <div id={`${baseId}-name-${moduleKey}`}>
                                            {item.displayName}
                                        </div>
                                    </OdxTableCell>
                                    <OdxTableCell id={`${baseId}-cell-${moduleKey}-description`}>
                                        {item.description}
                                    </OdxTableCell>
                                    <OdxTableCell id={`${baseId}-cell-${moduleKey}-usage`}>
                                        {ChipAmountOfUsedProjects(item)}
                                    </OdxTableCell>
                                </OdxTableRow>
                            )
                        })
                   }
                </OdxTable>
            </div>
        )
    }

    const toggleLayout = () => {
        setLayout(layout === Layout.Grid ? Layout.List : Layout.Grid);
    }
    const openModuleDocumentation = (module: IListStaticModule) => {
        setSelectedModule(module);
        setIsOpen(true);
    };

    return <>
        <div ref={containerRef} id={`${baseId}-view`}>
            {isOpen && selectedModule && (
                <ShowDocumentation isOpen={isOpen} onClose={() => setIsOpen(false)} selectedModule={selectedModule}/>
            )}

            <OdxAreaHeader text={'All Modules'}
                           subTitle={<>
                               <div id={`${baseId}-status`}>{backgroundLoading ? 'Loading modules' : availableModules.length + ' modules'}</div>
                               <div id={`${baseId}-instructions`}>
                                   This is the documentation for the modules. Click a module to get a closer look.
                               </div>
                           </>}
                           odxSize={'large'}
                           className={'!pl-0 !pr-0'}
                           id={`${baseId}-header`}

            />
            <div data-odxLayout='grid vertical-end'>
                <div data-odxLayout='offset-8'></div>
                <div data-odxLayout='4 vertical-end' className={'justify-self-end'}>
                    <OdxMainFilterGroup>
                        <odx-icon-button
                            className={"!mt-0"}
                            variant="ghost"
                            size="lg"
                            type="button"
                            icon={`${odxIconSets.core}::${Layout.Grid === layout ? 'dashboard' : 'menu'}`}
                            onClick={toggleLayout}
                            id={`OdxButton_${baseId}-layout-toggle`}
                        />
                    </OdxMainFilterGroup>
                </div>
            </div>
            {layout === Layout.Grid ? NewModuleCards() : getListView()}
        </div>
    </>

}

export default ModuleDocumentations;
